// Importing required modules from React Router for navigation
import { Navigate } from 'react-router-dom';

// Importing hooks from React Redux to dispatch actions and access state
import { useDispatch, useSelector } from 'react-redux';

// Importing React hooks
import { useState, useEffect } from 'react';

// Action constant for setting user details in Redux store
import { USER_DETAILS } from 'store/actions';

// Axios instance configured with base URL and headers
import { axiosInstance } from '../axios';

// For prop type validation
import PropTypes from 'prop-types';
import { CircularProgress } from '@mui/material';

// Async function to fetch user details from the server
const getData = async (signal) => {
    try {
        const response = await axiosInstance.get(`accounts/get-user-detail/`, { signal });
        return response.data;
    } catch (error) {
        if (error.code === 'ERR_CANCELED') {
            console.warn('User details request cancelled.');
        } else {
            console.error('Error fetching user details:', error);
        }
        return null;
    }
};

// ProtectedRoute component that restricts route access based on authentication
export const ProtectedRoute = ({ children }) => {
    const dispatch = useDispatch();
    const customization = useSelector((state) => state.customization);

    const [isAuthenticated, setIsAuthenticated] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        const signal = controller.signal;

        const checkUser = async () => {
            const refreshToken = localStorage.getItem('refresh_token');
            if (!refreshToken) {
                setIsAuthenticated(false); // No token → redirect to login
                return;
            }

            try {
                const tokenParts = JSON.parse(atob(refreshToken.split('.')[1]));
                const now = Math.ceil(Date.now() / 1000);

                if (tokenParts.exp > now) {
                    // Token is valid
                    if (customization?.userDetail?.length > 0) {
                        setIsAuthenticated(true);
                    } else {
                        const userData = await getData(signal);
                        if (userData) {
                            dispatch({
                                type: USER_DETAILS,
                                userDetail: [
                                    {
                                        user_detail: userData.user_detail,
                                        business_detail: userData.business_detail,
                                        role_detail: userData.role_detail,
                                        permission: userData.permission
                                    }
                                ]
                            });
                        }
                        // Regardless of API success, stay on page if token is valid
                        setIsAuthenticated(true);
                    }
                } else {
                    // Expired token
                    setIsAuthenticated(false);
                }
            } catch (err) {
                console.error('Token decode/API error:', err);
                setIsAuthenticated(false);
            }
        };

        checkUser();

        return () => controller.abort(); // cancel API request on component unmount
    }, [customization, dispatch]);

    if (isAuthenticated === null) {
        return (
            <div
                style={{
                    height: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: '10px'
                }}
            >
                <CircularProgress aria-label="Loading..." />
            </div>
        );
    }

    return isAuthenticated ? children : <Navigate to="/login" />;
};

// Type-checking for props
ProtectedRoute.propTypes = {
    children: PropTypes.node
};
